import { NavLink } from "react-router-dom";
import { usePermission } from "../../hooks/usePermission";

/**
 * Sidebar navigation — a flat list of single links (no dropdown groups).
 * Every `code` must match a tbmodules.code so the permission check
 * (`can(code, "is_view")`) decides visibility — a Centre Admin automatically
 * sees a trimmed menu. `type: "title"` entries are plain section headings.
 */
const NAV = [
  { type: "item", code: "dashboard", path: "/dashboard", label: "Dashboard", icon: "cil-speedometer" },

  { type: "title", label: "Families" },
  { type: "item", code: "families", path: "/families", label: "Families", icon: "cil-people", codes: ["families", "registrations", "children"] },

  { type: "title", label: "Forms" },
  { type: "item", code: "submissions", path: "/submissions", label: "Form Submissions", icon: "cil-inbox" },
  { type: "item", code: "forms",       path: "/forms",       label: "Forms Catalog",    icon: "cil-list-rich" },

  { type: "title", label: "Content" },
  { type: "item", code: "documents", path: "/documents", label: "Documents", icon: "cil-file", groupOnly: true },
  { type: "item", code: "policies",  path: "/policies",  label: "Policies",  icon: "cil-shield-alt", groupOnly: true },
  { type: "item", code: "handbook",  path: "/handbook",  label: "Handbook",  icon: "cil-book", groupOnly: true },

  { type: "title", label: "Administration" },
  { type: "item", code: "centres",  path: "/centres",  label: "Centres",      icon: "cil-location-pin" },
  { type: "item", code: "users",    path: "/users",    label: "Admin Users",  icon: "cil-user" },
  // { type: "item", code: "roles",    path: "/roles",    label: "Roles",        icon: "cil-lock-locked", groupOnly: true },
  // { type: "item", code: "activity", path: "/activity", label: "Activity Log", icon: "cil-clock", groupOnly: true },
  { type: "item", code: "settings", path: "/settings", label: "Settings",     icon: "cil-settings" },
];

export default function Sidebar() {
  const { can, isGroupAdmin } = usePermission();

  const canSee = (item) => {
    if (item.groupOnly && !isGroupAdmin) return false;
    if (item.always) return true;
    if (item.codes) return item.codes.some((code) => can(code, "is_view"));
    return can(item.code, "is_view");
  };

  // Keep visible items, then drop any section title that has no items after it.
  const visible = NAV.filter((entry) => entry.type !== "item" || canSee(entry));
  const nav = visible.filter((entry, i) => {
    if (entry.type !== "title") return true;
    const next = visible[i + 1];
    return next && next.type === "item";
  });

  const hasAnything = nav.some((e) => e.type === "item");

  const renderLink = (item) => (
    <li className="nav-item" key={item.path}>
      <NavLink
        to={item.path}
        end={item.path === "/dashboard"}
        className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}
      >
        <i className={`nav-icon ${item.icon}`} />
        {item.label}
      </NavLink>
    </li>
  );

  return (
    <div className="sidebar sidebar-dark sidebar-fixed border-end" id="sidebar">
      <div className="sidebar-header border-bottom">
        <div className="sidebar-brand">
          <span className="bw-brand-mark">S</span>
          <span className="d-flex flex-column">
            <span className="bw-brand-text">Stride</span>
            <span className="bw-brand-tag">Family Portal Admin</span>
          </span>
        </div>
        <button
          className="btn-close d-lg-none"
          type="button"
          data-coreui-theme="dark"
          aria-label="Close"
          onClick={() =>
            window.coreui?.Sidebar.getInstance(
              document.querySelector("#sidebar")
            )?.toggle()
          }
        />
      </div>

      <ul className="sidebar-nav">
        {!hasAnything && (
          <li className="nav-item">
            <span className="nav-link text-muted small">
              No accessible screens
            </span>
          </li>
        )}

        {nav.map((entry, i) =>
          entry.type === "title" ? (
            <li className="nav-title" key={`title-${entry.label}-${i}`}>
              {entry.label}
            </li>
          ) : (
            renderLink(entry)
          )
        )}
      </ul>

      <div className="sidebar-footer border-top d-none d-md-flex">
        <button
          className="sidebar-toggler"
          type="button"
          data-coreui-toggle="unfoldable"
        />
      </div>
    </div>
  );
}
